import { useEffect, useRef, useState, type FC } from 'react';
import { INDUSTRY_LINKS, MARKETING_SITE_URL } from '@/shared/constants/navigation';

// Escape hatch for a chromeless marketing one-pager. Hamburger button
// (fixed top-right) opens a full-screen overlay listing the rest of the
// site. Closed on Esc, click on backdrop, or any link selection.
// Hidden in print.
export const OnePagerMenu: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);

  // Close on Esc; restore focus to the trigger when closing.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const buttonElement = buttonRef.current;
    // Lock background scroll while overlay is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Move focus into the overlay for keyboard users.
    firstLinkRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      buttonElement?.focus();
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
        aria-controls="onepager-menu-overlay"
        onClick={() => setIsOpen((v) => !v)}
        className={[
          'print:hidden',
          'fixed top-4 right-4 z-30 md:top-6 md:right-6',
          'flex h-11 w-11 items-center justify-center',
          'rounded-full bg-unimo-onepager-bg/80 text-unimo-onepager-ink',
          'shadow-sm backdrop-blur-sm',
          'transition-colors duration-150',
          'hover:bg-unimo-onepager-ink/10',
          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-unimo-onepager-ink/20',
        ].join(' ')}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          aria-hidden="true"
        >
          {isOpen ? (
            <>
              <path d="M6 6l12 12" />
              <path d="M6 18L18 6" />
            </>
          ) : (
            <>
              <path d="M4 7h16" />
              <path d="M4 12h16" />
              <path d="M4 17h16" />
            </>
          )}
        </svg>
      </button>

      {isOpen && (
        <div
          id="onepager-menu-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          className="print:hidden fixed inset-0 z-20 bg-unimo-onepager-bg/95 backdrop-blur-sm"
          onClick={(e) => {
            // Close when clicking the backdrop (not when clicking a link).
            if (e.target === e.currentTarget) setIsOpen(false);
          }}
        >
          <nav className="flex h-full w-full flex-col items-center justify-center gap-8 px-6 text-unimo-onepager-ink">
            <a
              ref={firstLinkRef}
              href={MARKETING_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="text-[28px] font-semibold leading-tight hover:opacity-70"
            >
              Home <span className="text-[16px] font-normal opacity-60">(getunimo.com)</span>
            </a>

            <div className="flex flex-col items-center gap-4">
              <div className="text-[13px] uppercase tracking-[0.12em] opacity-50">Industries</div>
              {INDUSTRY_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className="text-[22px] font-normal hover:opacity-70"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </nav>
        </div>
      )}
    </>
  );
};
